/**
 * ChatMessageList Component
 * Scrollable list of chat messages with auto-scroll to bottom
 */

import { useEffect, useRef } from "react";
import { ChatMessage } from "./ChatMessage";
import { ThinkingIndicator } from "./ThinkingIndicator";
import type {
  AgentType,
  ChatMessage as ChatMessageType,
} from "@/types/chat.types";
import { cn } from "@/lib/utils";

interface ChatMessageListProps {
  messages: ChatMessageType[];
  agentType?: AgentType | null;
  className?: string;
  compact?: boolean;
  isStreaming?: boolean;
  isLoading?: boolean;
  conversationId?: string | null;
}

export const ChatMessageList = ({
  messages,
  agentType,
  className,
  compact = false,
  isStreaming = false,
  isLoading = false,
  conversationId,
}: ChatMessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(messages.length);

  // Auto-scroll only when:
  //   - a new message is appended (length increases), OR
  //   - the user was already near the bottom (didn't scroll up manually).
  // This prevents the stream/ref-change re-fires from yanking the user down
  // while they try to read earlier messages.
  useEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    const lengthIncreased = messages.length > prevLength;
    prevMessagesLengthRef.current = messages.length;

    const end = messagesEndRef.current;
    if (!end) return;

    // Walk up to find the actual scrolling ancestor
    let scrollParent: HTMLElement | null = end.parentElement;
    while (scrollParent) {
      const style = getComputedStyle(scrollParent);
      if (/(auto|scroll)/.test(style.overflowY)) break;
      scrollParent = scrollParent.parentElement;
    }

    const NEAR_BOTTOM_THRESHOLD = 120;
    const isNearBottom = scrollParent
      ? scrollParent.scrollHeight -
          scrollParent.scrollTop -
          scrollParent.clientHeight <
        NEAR_BOTTOM_THRESHOLD
      : true;

    if (lengthIncreased || isNearBottom) {
      end.scrollIntoView({
        behavior: lengthIncreased ? "smooth" : "auto",
      });
    }
  }, [messages]);

  if (messages.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn("flex-1 overflow-y-auto overflow-x-hidden", className)}
    >
      <div
        className={cn(
          "w-full space-y-0",
          compact ? "px-2" : "max-w-4xl mx-auto px-4"
        )}
      >
        {messages.map((message, idx) => (
          <ChatMessage
            key={message.id}
            message={message}
            agentType={
              message.role === "assistant" ? agentType ?? undefined : undefined
            }
            isLastMessage={idx === messages.length - 1}
            isStreaming={isStreaming}
            compact={compact}
            conversationId={conversationId}
          />
        ))}

        {/* Show thinking indicator immediately after user sends message */}
        {(() => {
          const lastUserMessage = [...messages]
            .reverse()
            .find((m) => m.role === "user");
          const lastAssistantMessage = [...messages]
            .reverse()
            .find((m) => m.role === "assistant");

          // Check if the last user message is more recent than the last assistant message
          // This means we just sent a new message and are waiting for response
          const lastUserIndex = messages.indexOf(lastUserMessage!);
          const lastAssistantIndex = lastAssistantMessage
            ? messages.indexOf(lastAssistantMessage)
            : -1;
          const waitingForResponse = lastUserIndex > lastAssistantIndex;

          // Check if message has real content (tools or text)
          const hasRealContent = lastAssistantMessage?.parts?.some(
            (part: any) =>
              part.type === "text" ||
              part.type === "tool-call" ||
              part.type === "dynamic-tool" ||
              part.type === "tool-code_execution"
          );

          // Show thinking if:
          // 1. We're loading OR streaming, AND
          // 2. Either we're waiting for a response (user message is last)
          //    OR the assistant message exists but has no real content yet
          const shouldShow =
            (isLoading || isStreaming) &&
            (waitingForResponse || !hasRealContent);

          return shouldShow ? <ThinkingIndicator /> : null;
        })()}

        {/* Invisible div for auto-scroll */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
